import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ConvoPartnerProfile, UpsertConvoPartnerProfileRequest } from '../../types/convo.js';
import * as convoApi from '../../services/convo-api.js';

@customElement('cp-convo-partner-profile')
export class CpConvoPartnerProfile extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) personId = 0;
  @property({ type: Object }) profile: ConvoPartnerProfile | null = null;

  @state() private _loading = false;
  @state() private _editing = false;
  @state() private _saving = false;
  @state() private _error: string | null = null;
  @state() private _editData: UpsertConvoPartnerProfileRequest = {};

  connectedCallback(): void {
    super.connectedCallback();
    if (this.personId && !this.profile) {
      void this._loadProfile();
    }
  }

  private async _loadProfile(): Promise<void> {
    if (!this.personId) return;
    this._loading = true;
    try {
      this.profile = await convoApi.getPartnerProfile(this.personId);
    } catch {
      // No profile yet
    } finally {
      this._loading = false;
    }
  }

  private _startEdit(): void {
    this._editData = {
      disc_profile: this.profile?.disc_profile ?? '',
      communication_style: this.profile?.communication_style ?? '',
      decision_making: this.profile?.decision_making ?? '',
      negotiation_style: this.profile?.negotiation_style ?? '',
      formality_level: this.profile?.formality_level ?? '',
      notes: this.profile?.notes ?? '',
    };
    this._editing = true;
  }

  private _cancelEdit(): void {
    this._editing = false;
    this._error = null;
  }

  private async _save(): Promise<void> {
    this._saving = true;
    this._error = null;
    try {
      this.profile = await convoApi.upsertPartnerProfile(this.personId, this._editData);
      this._editing = false;
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern';
    } finally {
      this._saving = false;
    }
  }

  private async _delete(): Promise<void> {
    try {
      await convoApi.deletePartnerProfile(this.personId);
      this.profile = null;
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Loeschen';
    }
  }

  render() {
    if (this._loading) {
      return html`<div class="cp-partner-profile"><p class="cp-partner-profile__loading">Lade Profil...</p></div>`;
    }

    return html`
      <div class="cp-partner-profile">
        <div class="cp-partner-profile__header">
          <h3 class="cp-partner-profile__title">Kommunikationsprofil</h3>
          <div class="cp-partner-profile__header-actions">
            ${!this._editing ? html`
              <button class="cp-partner-profile__btn" @click=${this._startEdit}>
                ${this.profile ? 'Bearbeiten' : 'Profil anlegen'}
              </button>
              ${this.profile ? html`
                <button class="cp-partner-profile__btn cp-partner-profile__btn--delete" @click=${this._delete}>Loeschen</button>
              ` : nothing}
            ` : nothing}
          </div>
        </div>

        ${this._error ? html`<div class="cp-partner-profile__error">${this._error}</div>` : nothing}

        ${this._editing ? this._renderEditView() : this._renderReadView()}
      </div>
    `;
  }

  private _renderReadView() {
    if (!this.profile) {
      return html`<p class="cp-partner-profile__empty">Kein Kommunikationsprofil hinterlegt</p>`;
    }

    const p = this.profile;
    return html`
      <div class="cp-partner-profile__grid">
        ${p.disc_profile ? html`<div class="cp-partner-profile__item"><span class="cp-partner-profile__label">DISC</span><span>${p.disc_profile}</span></div>` : nothing}
        ${p.communication_style ? html`<div class="cp-partner-profile__item"><span class="cp-partner-profile__label">Kommunikationsstil</span><span>${p.communication_style}</span></div>` : nothing}
        ${p.decision_making ? html`<div class="cp-partner-profile__item"><span class="cp-partner-profile__label">Entscheidungsstil</span><span>${p.decision_making}</span></div>` : nothing}
        ${p.negotiation_style ? html`<div class="cp-partner-profile__item"><span class="cp-partner-profile__label">Verhandlungsstil</span><span>${p.negotiation_style}</span></div>` : nothing}
        ${p.formality_level ? html`<div class="cp-partner-profile__item"><span class="cp-partner-profile__label">Formalitaet</span><span>${p.formality_level}</span></div>` : nothing}
        ${p.notes ? html`<div class="cp-partner-profile__item cp-partner-profile__item--full"><span class="cp-partner-profile__label">Notizen</span><span>${p.notes}</span></div>` : nothing}
      </div>
    `;
  }

  private _renderEditView() {
    return html`
      <div class="cp-partner-profile__edit">
        <div class="cp-partner-profile__row">
          <div class="cp-partner-profile__field">
            <label>DISC-Profil</label>
            <input type="text" placeholder="z.B. D/I, S/C" .value=${this._editData.disc_profile ?? ''}
              @input=${(e: Event) => { this._editData = { ...this._editData, disc_profile: (e.target as HTMLInputElement).value }; }}
              ?disabled=${this._saving} />
          </div>
          <div class="cp-partner-profile__field">
            <label>Kommunikationsstil</label>
            <input type="text" placeholder="z.B. direkt, analytisch" .value=${this._editData.communication_style ?? ''}
              @input=${(e: Event) => { this._editData = { ...this._editData, communication_style: (e.target as HTMLInputElement).value }; }}
              ?disabled=${this._saving} />
          </div>
        </div>
        <div class="cp-partner-profile__row">
          <div class="cp-partner-profile__field">
            <label>Entscheidungsstil</label>
            <input type="text" placeholder="z.B. logisch, konsens" .value=${this._editData.decision_making ?? ''}
              @input=${(e: Event) => { this._editData = { ...this._editData, decision_making: (e.target as HTMLInputElement).value }; }}
              ?disabled=${this._saving} />
          </div>
          <div class="cp-partner-profile__field">
            <label>Verhandlungsstil</label>
            <input type="text" placeholder="z.B. kollaborativ, kompetitiv" .value=${this._editData.negotiation_style ?? ''}
              @input=${(e: Event) => { this._editData = { ...this._editData, negotiation_style: (e.target as HTMLInputElement).value }; }}
              ?disabled=${this._saving} />
          </div>
        </div>
        <div class="cp-partner-profile__field">
          <label>Formalitaet</label>
          <input type="text" placeholder="z.B. formal, casual" .value=${this._editData.formality_level ?? ''}
            @input=${(e: Event) => { this._editData = { ...this._editData, formality_level: (e.target as HTMLInputElement).value }; }}
            ?disabled=${this._saving} />
        </div>
        <div class="cp-partner-profile__field">
          <label>Notizen</label>
          <textarea rows="3" placeholder="Freitext Notizen zum Kommunikationsstil..." .value=${this._editData.notes ?? ''}
            @input=${(e: Event) => { this._editData = { ...this._editData, notes: (e.target as HTMLTextAreaElement).value }; }}
            ?disabled=${this._saving}></textarea>
        </div>
        <div class="cp-partner-profile__edit-actions">
          <button class="cp-partner-profile__btn" @click=${this._cancelEdit} ?disabled=${this._saving}>Abbrechen</button>
          <button class="cp-partner-profile__btn cp-partner-profile__btn--save" @click=${this._save} ?disabled=${this._saving}>
            ${this._saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-convo-partner-profile': CpConvoPartnerProfile;
  }
}
